import { FC, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { EditIcon, EyeIconSmall, ThreeDotsIcon } from '../../../../../assets/icons/index';
import { AlertDialogWrapper } from '../../../../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import Button, { ButtonDisableStyle } from '../../../../../components/button/index';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '../../../../../components/popover/index';
import { Tooltip } from '../../../../../components/tooltip/index';
import { Company, EventHooks, useFormat, useModalState } from '../../../../../hooks/index';
import { CompanyIncorporationEventItem, EventTypeBackend } from '../../../../../types/events.types';
import {
  downloadCompanyStorageFilesSequentially,
  openCompanyStorageUrlInNewTab,
} from '../../../../../utils/company-storage-url.util';
import { toNumber } from '../../../../../utils/to-number.util';
import { EventTransactionFilesCell } from '../../../components/event-transaction-files-cell';
import { ManageReverseVestingMenuItem } from '../../../manage-reverse-vesting/index';
import { FilesModal } from '../../../modals/index';
import { resolveTransactionDisplayDate } from '../../../utils/resolve-transaction-display-date.util';
import { BlockedTooltipAction } from '../blocked-action-tooltip';
import { BasicTable } from './basic-table';
import {
  getTransactionsTableStateProps,
  itemsToFetchMap,
  TableBodyProps,
} from './event-table-types.types';
import { PaginationItem } from './pagination-item';
import { Td } from './table-td';

export const CompanyIncorporation: FC<TableBodyProps> = (props) => {
  const { format } = useFormat();
  const { selectedCompany: { currency } = {} } = Company.useSelected();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsToFetch, setItemsToFetch] = useState(itemsToFetchMap[0]);
  const [activeFilesModal, setActiveFilesModal] = useState<{
    createdAt?: Date;
    filesLinks: string[];
    stakeholderName: string;
  } | null>(null);
  const { items, totalPages, isLoading } =
    EventHooks.useTransactions<CompanyIncorporationEventItem>({
      eventId: props.id,
      eventType: EventTypeBackend.COMPANY_INCORPORATION,
      companyId: props.companyId,
      currentPage,
      take: Number(itemsToFetch),
      dateFilter: props.selectedDateFilter,
      customDateFilter: props.selectedCustomDateFilter,
      transactionView: props.transactionView,
    });

  const tableStateProps = getTransactionsTableStateProps({
    selectedDateFilter: props.selectedDateFilter,
    selectedCustomDateFilter: props.selectedCustomDateFilter,
    transactionView: props.transactionView,
    isLoading,
    itemsCount: items.length,
    event: props,
    itemsToFetch: Number(itemsToFetch),
  });

  const {
    isOpen: isOpenFilesModal,
    handleCloseModal: handleCloseFilesModal,
    handleOpenModal: handleOpenFilesModal,
    toggler: filesModalToggler,
  } = useModalState();

  const handleDocumentClick = (link: string) => {
    void openCompanyStorageUrlInNewTab(props.companyId, link);
  };

  const handleDownloadAllDocuments = async (links: string[]) => {
    await downloadCompanyStorageFilesSequentially(props.companyId, links);
  };

  if (props.type !== EventTypeBackend.COMPANY_INCORPORATION) return <></>;

  const { onView, onEdit, eventId, inCurrentRound, transactionView, parValue, name } = props;
  const parsedParValue = toNumber(parValue) ?? 0;
  const currencySymbol = currency?.symbol ?? '$';

  const formatCurrency = (value: number) =>
    `${currencySymbol}${value.toLocaleString('en-US', { maximumFractionDigits: 7 })}`;

  const handleOpenFilesModalForRow = (
    filesLinks: string[],
    stakeholderName: string,
    date: Date,
  ) => {
    setActiveFilesModal({ filesLinks, stakeholderName, createdAt: date });
    handleOpenFilesModal();
  };

  const handleCloseFilesModalWithReset = () => {
    setActiveFilesModal(null);
    handleCloseFilesModal();
  };

  return (
    <>
      <AlertDialogWrapper control={{ onOpenChange: filesModalToggler, open: isOpenFilesModal }}>
        <FilesModal
          createdAt={activeFilesModal?.createdAt}
          filesLinks={activeFilesModal?.filesLinks || []}
          name={name}
          onClose={handleCloseFilesModalWithReset}
          onDownloadFile={handleDocumentClick}
          onSuccess={() => handleDownloadAllDocuments(activeFilesModal?.filesLinks || [])}
          stakeholderName={activeFilesModal?.stakeholderName || ''}
        />
      </AlertDialogWrapper>
      <BasicTable
        eventType={EventTypeBackend.COMPANY_INCORPORATION}
        transactionView={transactionView}
        {...tableStateProps}
      >
        {items.map(
          (
            {
              date,
              stakeholder,
              sharesCount,
              shareClass,
              id: transactionId,
              reverseVesting,
              filesLinks,
            },
            index,
          ) => {
            const considerationPay = sharesCount * parsedParValue;
            const stakeholderName = stakeholder?.fullName ?? '';
            // A terminated stakeholder's transaction is settled history — no edit.
            const isTerminatedRow = Boolean(stakeholder?.isTerminated);
            const canModifyTransaction = inCurrentRound && !isTerminatedRow;
            const blockedMessageKey = isTerminatedRow
              ? 'EVENT_TERMINATED_STAKEHOLDER_ACTION_BLOCKED'
              : undefined;

            return (
              <tr key={`${index}_${transactionId}`}>
                <Td
                  value={format(
                    resolveTransactionDisplayDate({ date, reverseVesting }),
                    'dd/MM/yyyy',
                  )}
                />
                <Td value={<Tooltip content={stakeholderName}>{stakeholderName}</Tooltip>} />
                <Td
                  value={
                    <Tooltip content={shareClass?.name ?? ''}>{shareClass?.name ?? ''}</Tooltip>
                  }
                />
                <Td value={toNumber(sharesCount)?.toLocaleString('en-US') || ''} />
                <Td value={formatCurrency(parsedParValue)} />
                <Td value={formatCurrency(considerationPay)} />
                {!transactionView && (
                  <>
                    <Td
                      value={
                        <EventTransactionFilesCell
                          date={date}
                          filesLinks={filesLinks}
                          onDocumentClick={handleDocumentClick}
                          onOpenFilesModal={handleOpenFilesModalForRow}
                          stakeholderName={stakeholderName}
                        />
                      }
                    />
                    <Td
                      className="w-12"
                      value={
                        <Popover>
                          <PopoverTrigger className="h-fit w-fit rounded data-[state=open]:shadow-md">
                            <div className="flex size-6 rotate-90 items-center justify-center rounded bg-transparent transition-colors hover:bg-gray-100">
                              <ThreeDotsIcon />
                            </div>
                          </PopoverTrigger>
                          <PopoverContent className="flex w-fit flex-col gap-[2px] rounded p-2 font-normal shadow-2xl [&>*]:font-inter [&>*]:font-normal">
                            <PopoverClose asChild>
                              <div
                                className="flex w-full cursor-pointer items-center gap-2 rounded p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                                onClick={() => onView(eventId, transactionId)}
                              >
                                <EyeIconSmall />
                                <span className="text-sm font-normal text-gray-700">
                                  <AppFormattedMessage id="VIEW_DETAILS" />
                                </span>
                              </div>
                            </PopoverClose>
                            <BlockedTooltipAction
                              allowed={canModifyTransaction}
                              messageKey={blockedMessageKey}
                            >
                              <PopoverClose asChild>
                                <Button
                                  className="flex w-full items-center justify-start gap-2 rounded p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                                  data-permission-required
                                  disabled={!canModifyTransaction}
                                  disabledStyle={ButtonDisableStyle.NONE}
                                  onClick={() => onEdit(eventId, transactionId)}
                                  styleType="NONE"
                                >
                                  <EditIcon
                                    iconColor={!canModifyTransaction ? '#98A2B3' : undefined}
                                  />
                                  <span
                                    className={twMerge(
                                      'text-sm font-normal text-gray-700',
                                      !canModifyTransaction && 'text-gray-400',
                                    )}
                                  >
                                    <AppFormattedMessage id="EDIT_DETAILS" />
                                  </span>
                                </Button>
                              </PopoverClose>
                            </BlockedTooltipAction>
                            <ManageReverseVestingMenuItem
                              blockedMessageKey={blockedMessageKey}
                              disabled={isTerminatedRow}
                              eventId={eventId}
                              eventType={EventTypeBackend.COMPANY_INCORPORATION}
                              transactionId={transactionId}
                            />
                          </PopoverContent>
                        </Popover>
                      }
                    />
                  </>
                )}
              </tr>
            );
          },
        )}
      </BasicTable>
      <PaginationItem
        currentPage={currentPage}
        handleChangeCurrentPage={setCurrentPage}
        handleFetchAmountChange={setItemsToFetch}
        itemsCount={items.length}
        itemsToFetch={itemsToFetch}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
      />
    </>
  );
};
